import React, { useState } from "react";
import { Field, useFormikContext } from "formik";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import useAuth from "../../../../hooks/useAuth";
import labels from "../../../../constants/displayFields";
import useBlackoutDates from "../../../../hooks/shopping/useBlackoutDates";
import {
  getRestrictedDays,
  isDateRestricted,
} from "../../../../views/inventory/requests/orders/utils";
import useRoles from "../../../../hooks/user/useRoles";

export default function DeliveryDateField({ minDate, disabled = false }) {
  const { user } = useAuth();
  const [openDeliveryDate, setOpenDeliveryDate] = useState(false);
  const { values, setFieldValue } = useFormikContext();
  const dateName = user.company.settings.date_name;
  const { blackoutDates, refetch } = useBlackoutDates();
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const restrictedDays = isAdminOrManager ? [] : getRestrictedDays(blackoutDates);
  const isRestrictedDate =
    !isAdminOrManager &&
    values.deliveryDate &&
    isDateRestricted(blackoutDates, moment(values.deliveryDate).format("YYYY-MM-DD"));

  const shouldDisableDate = date => {
    const formattedDate = date.format("YYYY-MM-DD");
    return restrictedDays.some(day => day.date === formattedDate);
  };

  const handleOpen = () => {
    if (disabled) return;
    refetch();
    setOpenDeliveryDate(true);
  };

  const CustomDay = (props) => {
    const { day, ...other } = props;
    const isDisabled = shouldDisableDate(day);

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(isDisabled && {
            color: "red !important",
            textDecoration: "line-through",
            backgroundColor: "transparent",
          }),
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Field name="deliveryDate">
        {({ field, form, meta }) => {
          const errorMessage = meta.error || form.errors[field.name];
          const error = (meta.touched || form.submitCount > 0) && errorMessage;
          const hasError = error || isRestrictedDate;
          const helperText = isRestrictedDate
            ? "Selected date has been marked as unavailable"
            : errorMessage;

          return (
            <DatePicker
              open={openDeliveryDate}
              onOpen={handleOpen}
              onClose={() => setOpenDeliveryDate(false)}
              label={dateName || labels.order_delivery_date}
              minDate={minDate}
              value={values.deliveryDate ? moment(values.deliveryDate) : null}
              disabled={disabled}
              shouldDisableDate={shouldDisableDate}
              onChange={date => {
                const dateFormat = "yyyy-MM-DDTHH:mm:ss";
                setFieldValue("deliveryDate", moment(date).format(dateFormat));
              }}
              slots={{ day: CustomDay }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  variant: "outlined",
                  error: hasError,
                  helperText: helperText,
                  autoComplete: "off",
                  disabled: disabled,
                  onClick: () => {
                    if (!disabled) setOpenDeliveryDate(true);
                  },
                },
              }}
            />
          );
        }}
      </Field>
    </LocalizationProvider>
  );
}
